/**
 * FIV-499: the ONLY place trackers may be started. index.html must contain no
 * third-party script tags (enforced by scripts/check-consent-gating.mjs).
 */
import { getConsent, getEffectiveConsent, onConsentChange, setConsent } from "./consent";
import { captureFirstTouch } from "@/lib/analytics/attribution";
import { registerRumSink } from "@/lib/analytics/tracker";
import { isNoiseRumError } from "./rumNoise";
import { isAppContext } from "@/lib/appContext";

const GA_ID = "G-RZ6WEV9EYF";

// EEA + UK + CH ISO 3166-1 codes. Google's server-side geolocation enforces
// denied-by-default for visitors in these countries even when our timezone
// heuristic (region.ts) misclassified them (VPN, traveler).
const GA_OPT_IN_REGIONS = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH",
  // French outermost regions — Google geolocates these separately from FR:
  "GP", "MQ", "GF", "MF", "RE", "YT",
  // Åland, Svalbard — geolocate separately from FI/NO:
  "AX", "SJ",
];

// ZoomInfo WebSights tag (ZIProjectKey a64d2942931782145021). Vendor-obfuscated,
// copied verbatim from the old index.html — do not reformat.
// prettier-ignore
const ZOOMINFO_SNIPPET = `window[(function(_sXK,_bb){var _wzxtt='';for(var _rkoADI=0;_rkoADI<_sXK.length;_rkoADI++){var _Ms9k=_sXK[_rkoADI].charCodeAt();_Ms9k-=_bb;_Ms9k!=_rkoADI;_wzxtt==_wzxtt;_Ms9k+=61;_Ms9k%=94;_bb>2;_Ms9k+=33;_wzxtt+=String.fromCharCode(_Ms9k)}return _wzxtt})(atob('ZlVcfnt2cW8iV3En'), 12)] = 'a64d2942931782145021';     var zi = document.createElement('script');     (zi.type = 'text/javascript'),     (zi.async = true),     (zi.src = (function(_qM1,_SV){var _WBcZK='';for(var _Fjusp9=0;_Fjusp9<_qM1.length;_Fjusp9++){_WBcZK==_WBcZK;var _irRj=_qM1[_Fjusp9].charCodeAt();_irRj-=_SV;_irRj+=61;_irRj!=_Fjusp9;_irRj%=94;_SV>7;_irRj+=33;_WBcZK+=String.fromCharCode(_irRj)}return _WBcZK})(atob('JzMzLzJXTEwpMks5KEoyIjEoLzMySyIuLEw5KEozfiZLKTI='), 29)),     document.readyState === 'complete'?document.body.appendChild(zi):     window.addEventListener('load', function(){         document.body.appendChild(zi)     });`;

let gaLoaded = false;
let ddLoaded = false;
let ziLoaded = false;
let loaderStarted = false;
let reloadPage: () => void = () => window.location.reload();
let rumRef: { setTrackingConsent?: (consent: "granted" | "not-granted") => void } | null = null;

export function __setReloadForTests(fn: () => void): void {
  reloadPage = fn;
}
export function __resetLoaderForTests(): void {
  gaLoaded = ddLoaded = ziLoaded = loaderStarted = false;
  reloadPage = () => window.location.reload();
  rumRef = null;
}

export function loadGoogleAnalytics(): void {
  if (isAppContext() || gaLoaded || typeof document === "undefined") return;
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // GA's documented snippet pushes the arguments object, not an array.
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
  };
  // Region-scoped Consent Mode v2 defaults — most specific wins: denied for
  // EEA/UK/CH, granted elsewhere. Ad signals stay denied everywhere (we run
  // no Google ads features). An explicit user choice later issues an update.
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    region: GA_OPT_IN_REGIONS,
  });
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  });
  // Consent Mode state is per-page-load: a returning visitor with a stored
  // explicit grant must have Google's region-scoped denied default lifted on
  // every boot — and BEFORE config, so the session's first page_view isn't a
  // cookieless ping. Keyed on the STORED choice only — never effective
  // consent — so the geo backstop stays intact for implicit regional grants.
  if (getConsent()?.functional) {
    window.gtag("consent", "update", { analytics_storage: "granted" });
  }
  window.gtag("js", new Date());
  window.gtag("config", GA_ID);
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

export function loadDatadogRum(): void {
  if (isAppContext() || ddLoaded || !import.meta.env.PROD) return;
  ddLoaded = true;
  // Deferred dynamic import — keeps the ~100KB SDK out of the eager bundle and
  // off the critical path (moved verbatim from main.tsx pre-FIV-499).
  const initDatadog = () => {
    if (isAppContext()) return;
    import("@datadog/browser-rum")
      .then(({ datadogRum }) => {
        if (isAppContext()) return;
        datadogRum.init({
          applicationId: "6b6d4003-ddd1-4c5b-a5f7-837fbdd0f894",
          clientToken: "pub5c8ee44944e9ad0d89cb3c503b18497b",
          site: "datadoghq.com",
          service: "fivestar-landing-page",
          env: import.meta.env.MODE,
          version: __APP_VERSION__,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackResources: true,
          trackUserInteractions: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "mask-user-input",
          // FIV-499: this code path only runs after effective Functional
          // consent (explicit or regional default).
          trackingConsent: "granted",
          beforeSend: (event) => !isNoiseRumError(event),
        });
        registerRumSink(datadogRum);
        rumRef = datadogRum;
      })
      .catch(() => {
        // RUM is best-effort telemetry; a failed load must never break the app.
      });
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(initDatadog, { timeout: 3000 });
  } else {
    setTimeout(initDatadog, 1500);
  }
}

export function loadZoomInfo(): void {
  if (isAppContext() || ziLoaded || typeof document === "undefined") return;
  ziLoaded = true;
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.textContent = ZOOMINFO_SNIPPET;
  document.body.appendChild(s);
}

export function applyConsent(
  c: { functional: boolean; advertising: boolean } | null = getEffectiveConsent(),
): void {
  if (!c || isAppContext()) return;
  if (c.functional) {
    captureFirstTouch(); // no-op if already captured this session
    loadGoogleAnalytics();
    loadDatadogRum();
  }
  if (c.advertising) loadZoomInfo();
}

/** Boot entry: honor the effective consent now, and every later grant immediately.
 *  Singleton — subsequent calls are no-ops so double-mounting in StrictMode
 *  or hot-reload cannot register the listener twice. */
export function startConsentLoader(): void {
  if (loaderStarted) return;
  loaderStarted = true;
  if (isAppContext()) {
    // A prior ordinary website visit may have left first-party measurement
    // identifiers in this browser. App-origin forms must not reuse them.
    purgeTrackingState();
    return;
  }
  applyConsent();
  onConsentChange((c) => applyConsent(c));
  // A prior session's Datadog teardown can rewrite _dd_s after our purge;
  // re-expire it on any boot where effective functional consent is denied.
  if (!getEffectiveConsent().functional) expireCookie("_dd_s");
}

function expireCookie(name: string): void {
  try {
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.fivestar.video`;
  } catch {
    // SecurityError in cookie-blocked contexts (e.g. cross-origin iframes) — nothing to expire
  }
}

/** Best-effort removal of first-party tracking state. Third-party cookies
 *  (doubleclick IDE, openx i, clickagy) are not deletable by us — they simply
 *  stop being set once their parent scripts no longer load. */
function purgeTrackingState(): void {
  for (const name of ["_ga", `_ga_${GA_ID.replace("G-", "")}`, "_dd_s", "_zitok", "visitorId"]) {
    expireCookie(name);
  }
  try {
    window.sessionStorage.removeItem("fs_first_touch");
    window.sessionStorage.removeItem("fivestar-beacon-session");
  } catch {
    // session storage unavailable — continue purging local storage independently
  }
  try {
    window.localStorage.removeItem("fs_visitor_id");
    window.localStorage.removeItem("fs_active_variant");
    window.localStorage.removeItem("fs_exp_state");
  } catch {
    // storage unavailable — nothing to purge
  }
}

/** Stop an ordinary site's live SDKs before replacing it with an app document.
 * The visitor's saved website consent remains unchanged. */
export function stopTrackingForAppNavigation(): void {
  try {
    window.gtag?.("consent", "update", { analytics_storage: "denied", ad_storage: "denied" });
  } catch {
    // Stop the other SDK independently even if the Google tag throws.
  }
  try {
    rumRef?.setTrackingConsent?.("not-granted");
  } catch {
    // The document replacement is the final guarantee if an SDK throws.
  }
  purgeTrackingState();
}

/** The single write path for the UI. Downgrades — including from a regional
 *  implicit grant, where trackers are already running — purge + hard-reload so
 *  live SDKs cannot keep firing; pure grants start trackers in place. */
export function updateConsent(functional: boolean, advertising: boolean): void {
  if (isAppContext()) return;
  const prev = getEffectiveConsent();
  const next = setConsent(functional, advertising);
  const downgraded = (prev.functional && !functional) || (prev.advertising && !advertising);
  if (downgraded) {
    // Tell a live gtag to stop writing before we purge, so a queued hit can't
    // re-mint _ga* between purge and navigation.
    window.gtag?.("consent", "update", { analytics_storage: "denied", ad_storage: "denied" });
    // Stop a live RUM SDK before the reload so its unload flush doesn't
    // transmit post-withdrawal and its teardown can't rewrite _dd_s.
    try {
      rumRef?.setTrackingConsent?.("not-granted");
    } catch {
      // best-effort — the reload is the hard guarantee
    }
    // Any downgrade purges ALL first-party tracking state, even categories still granted —
    // privacy-safe over-purge; identifiers regenerate on reload for granted categories.
    try {
      purgeTrackingState();
    } finally {
      reloadPage();
    }
    return;
  }
  // Grant (or no-op) path: start any newly-permitted trackers right now.
  // Idempotent — each load* guards itself, so a startConsentLoader listener
  // firing applyConsent for the same change is harmless.
  applyConsent(next);
  // An EEA visitor (real, or misclassified by our heuristic) has GA under
  // Google's region-scoped denied default; their explicit grant lifts it.
  if (functional) window.gtag?.("consent", "update", { analytics_storage: "granted" });
}
